import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { omit } from "lodash";
import { MY_PROFILE } from "@/api/queries/myProfile";
import type { MyProfileQuery } from "@/__generated__/graphql";

type MyProfileQueryResult = MyProfileQuery["me"];

export type MyProfile = Omit<
  MyProfileQueryResult,
  "profilePhoto" | "officialOrganization"
> & {
  profilePhotoUrl: string | null;
  ssoEnabled: boolean | null;
};

export const useMyProfile = () => {
  const { data, loading, error } = useQuery(MY_PROFILE);

  const profile = useMemo(() => {
    if (!data?.me) {
      return null;
    }

    const me = data.me;

    return {
      ...omit(me, "profilePhoto", "officialOrganization"),
      locationCity: me.locationCity
        ? {
            id: me.locationCity.id,
            displayString: me.locationCity.displayString,
          }
        : null,
      interests:
        me.interests?.nodes
          ?.filter((node): node is NonNullable<typeof node> => node !== null)
          .map((node) => ({
            id: node.id,
            name: node.name,
          })) ?? [],
      majors:
        me.majors?.nodes
          ?.filter((node): node is NonNullable<typeof node> => node !== null)
          .map((node) => ({
            id: node.id,
            name: node.name,
          })) ?? [],
      profilePhotoUrl: me.profilePhoto?.url ?? null,
      ssoEnabled: me.officialOrganization?.ssoEnabled ?? null,
    };
  }, [data]);

  return {
    profile,
    loading,
    error,
  };
};
